"use client";

// /build-income — the real destination behind "Eigenes Business aufbauen"
// (was a /coming-soon fake door until 2026-07-03). Track 1 (Dropshipping) is a
// live sample course rendered as the inverse hero (the system's "now"
// treatment, cf. JobsV2 .applyHero); the other three tracks capture per-track
// demand with a persisted notify-me. Events:
//   ck_build_income_view          { src }
//   ck_build_income_track_select  { track, available }
//   ck_coming_soon_interest       { feature: "build_income", track, src } (waitlist joins,
//                                 kept in the coming_soon series for continuity)
export const dynamic = "force-dynamic";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { ArrowRight, Award, BookOpen, Check, Clapperboard, Clock3, GraduationCap, Link2, type LucideIcon } from "lucide-react";
import { Icon } from "@/design";
import { T } from "@/components/t";
import { track } from "@/lib/analytics";
import { storage } from "@/lib/storage";
import styles from "./BuildIncome.module.css";

const COURSE_HREF = "/learn/v2/course?sample=dropshipping";

const CHAPTERS: Array<{ de: string; en: string }> = [
  { de: "Das Modell", en: "The model" },
  { de: "Recht & Steuern", en: "Legal & taxes" },
  { de: "Produktrecherche", en: "Product research" },
  { de: "Shop-Aufbau", en: "Store setup" },
  { de: "Marketing & Ads", en: "Marketing & ads" },
  { de: "Kennzahlen & Betrieb", en: "Metrics & operations" },
];

type WaitlistTrack = {
  key: string;
  icon: LucideIcon;
  de: string;
  en: string;
  descDe: string;
  descEn: string;
};

const WAITLIST_TRACKS: WaitlistTrack[] = [
  {
    key: "creator",
    icon: Clapperboard,
    de: "Creator & Content",
    en: "Creator & content",
    descDe: "Mit Inhalten auf TikTok, YouTube und Instagram ein Publikum aufbauen und monetarisieren.",
    descEn: "Build and monetise an audience on TikTok, YouTube and Instagram.",
  },
  {
    key: "coaching",
    icon: GraduationCap,
    de: "Coaching & Freelancing",
    en: "Coaching & freelancing",
    descDe: "Dein Wissen als Dienstleistung verkaufen: Angebot, Preise, erste Kunden.",
    descEn: "Sell your skills as a service: offer, pricing, first clients.",
  },
  {
    key: "affiliate",
    icon: Link2,
    de: "Affiliate Marketing",
    en: "Affiliate marketing",
    descDe: "Produkte anderer empfehlen und an Provisionen verdienen, seriös statt Spam.",
    descEn: "Recommend products and earn commissions, the legitimate way.",
  },
];

// { [track]: true } — which waitlists this browser already joined.
const NOTIFIED_KEY = "ck.build_income.notified";

function readNotified(): Record<string, boolean> {
  try {
    return storage.getJSON<Record<string, boolean>>(NOTIFIED_KEY, {}) || {};
  } catch {
    return {};
  }
}

export default function BuildIncomePage() {
  const router = useRouter();
  const params = useSearchParams();
  const src = params.get("src") || "direct";
  // SSR + first client render = nothing joined; stored state resolves after
  // mount (same hydration-safe pattern as /coming-soon).
  const [notified, setNotified] = useState<Record<string, boolean>>({});

  useEffect(() => { setNotified(readNotified()); }, []);
  useEffect(() => { try { track("build_income_view", { src }); } catch { /* ignore */ } }, [src]);

  function onStartCourse() {
    try { track("build_income_track_select", { track: "dropshipping", available: true }); } catch { /* ignore */ }
    router.push(COURSE_HREF);
  }

  function onJoinWaitlist(trackKey: string) {
    if (notified[trackKey]) return;
    try { track("build_income_track_select", { track: trackKey, available: false }); } catch { /* ignore */ }
    try { track("coming_soon_interest", { feature: "build_income", track: trackKey, src: "build_income_hub" }); } catch { /* ignore */ }
    const next = { ...readNotified(), [trackKey]: true };
    try { storage.setJSON(NOTIFIED_KEY, next); } catch { /* ignore */ }
    setNotified(next);
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.eyebrow}>Build your income</span>
        <h1 className={styles.h1}>
          <T de="Eigenes Business aufbauen" en="Build your own income" />
        </h1>
        <p className={styles.sub}>
          <T
            de="Vier Wege zu eigenem Einkommen neben Job oder Jobsuche. Mit echten Zahlen, ohne Get-rich-quick-Versprechen."
            en="Four paths to your own income alongside your job or job search. Real numbers, no get-rich-quick promises."
          />
        </p>
      </header>

      <section className={styles.hero} aria-labelledby="bi-course-title">
        <span className={styles.heroTag}>
          <T de="Neuer Kurs · Kostenlos" en="New course · Free" />
        </span>
        <h2 id="bi-course-title" className={styles.heroTitle}>
          <T de="Dropshipping: Eigenes Business starten" en="Dropshipping: start your own business" />
        </h2>
        <p className={styles.heroBody}>
          <T
            de="Von der Idee zum ersten Verkauf: Geschäftsmodell, Recht und Steuern, Produktwahl, Shop und Werbung. Ehrlich gerechnet, inklusive der Zahlen, die dir niemand zeigt."
            en="From idea to first sale: the business model, legal and tax basics, product research, store and ads. Honestly calculated, including the numbers nobody shows you."
          />
        </p>
        <div className={styles.heroMeta}>
          <span className={styles.heroMetaItem}>
            <Icon icon={BookOpen} decorative size={15} />
            <T de="6 Kapitel" en="6 chapters" />
          </span>
          <span className={styles.heroMetaItem}>
            <Icon icon={Clock3} decorative size={15} />
            <T de="ca. 2 Stunden" en="approx. 2 hours" />
          </span>
          <span className={styles.heroMetaItem}>
            <Icon icon={Award} decorative size={15} />
            <T de="Mit Zertifikat" en="With certificate" />
          </span>
        </div>
        <ol className={styles.chapters}>
          {CHAPTERS.map((c, i) => (
            <li key={c.de} className={styles.chapter}>
              <span className={styles.chapterNum}>{i + 1}</span>
              <T de={c.de} en={c.en} />
            </li>
          ))}
        </ol>
        <div className={styles.heroActions}>
          <button type="button" className={styles.heroCta} onClick={onStartCourse}>
            <T de="Kurs starten" en="Start course" />
            <Icon icon={ArrowRight} decorative size={16} />
          </button>
          <span className={styles.heroHint}>
            <T de="Läuft direkt im Lern-Hub, dein Fortschritt wird gespeichert." en="Runs in the learning hub, your progress is saved." />
          </span>
        </div>
      </section>

      <section aria-labelledby="bi-tracks-title" className={styles.tracks}>
        <div className={styles.tracksHead}>
          <h2 id="bi-tracks-title" className={styles.tracksTitle}>
            <T de="Was sollen wir als Nächstes bauen?" en="What should we build next?" />
          </h2>
          <p className={styles.tracksSub}>
            <T de="Sag uns, welcher Weg dich interessiert. Der Kurs mit den meisten Stimmen kommt zuerst." en="Tell us which path interests you. The course with the most votes ships first." />
          </p>
        </div>
        <div className={styles.grid}>
          {WAITLIST_TRACKS.map((t) => {
            const joined = !!notified[t.key];
            return (
              <article key={t.key} className={styles.card}>
                <span className={styles.cardIcon}>
                  <Icon icon={t.icon} decorative size={20} />
                </span>
                <h3 className={styles.cardTitle}><T de={t.de} en={t.en} /></h3>
                <p className={styles.cardDesc}><T de={t.descDe} en={t.descEn} /></p>
                {joined ? (
                  <span role="status" className={styles.joined}>
                    <Icon icon={Check} decorative size={15} />
                    <T de="Du stehst auf der Liste!" en="You're on the list!" />
                  </span>
                ) : (
                  <button type="button" className={styles.cardBtn} onClick={() => onJoinWaitlist(t.key)}>
                    <T de="Sag mir Bescheid" en="Notify me" />
                  </button>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
